/* istanbul ignore file */
import { fireTag } from '@vue-util/tagManager';
import { RETURN_METHOD_KEY_MAP, RETURN_RESOLUTIONS } from 'config';

const getPrice = (price, currency = true) => {
  if (!price) return '';
  if (price?.startsWith('$') && !currency) return price.substring(1);
  if (!price?.startsWith('$') && currency) return `$${price}`;
  return price;
};

const buildKey = (item) => {
  switch (item?.returnType) {
    case 'VR':
    case 'VE':
      return `${item?.upc}-${item?.returnType}-${item?.virtualReturnExpectedBackDate}`;
    case 'PR':
      return `${item?.upc}-${item?.returnType}`;
    default:
      return `${item?.upc}`;
  }
};

const formatPrice = (value) =>
  value?.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const prepareReturnItemsProperty = (item = { itemDetails: {} }) => {
  const key = buildKey(item);
  if (item.returnType === 'PR' && item.returnQuantity === 1) {
    return {
      upc: item.upc,
      ...(item.itemDetails.returnChannel && {
        returnChannel: item.itemDetails.returnChannel,
      }),
      color: item.itemDetails.upcColor,
      size: item.itemDetails.upcSize,
      unitPrice: item.unitPrice,
      returnType: item.returnType,
      quantity: item.returnQuantity,
      fulfillmentStore: item.fulfillmentStore,
      fulfillmentChannel: item.fulfillmentChannel, // this property may not be always available in step 1 response
      fulfillmentType: item.fulfillmentType,
      originalQty: item.originalQty,
      productId: item.itemDetails.productId,
      isDiscountOrCouponApplied: item.isDiscountOrCouponApplied,
      key,
    };
  }
  if (item.returnType !== 'PR') {
    return {
      upc: item.upc,
      ...(item.itemDetails.returnChannel && {
        returnChannel: item.itemDetails.returnChannel,
      }),
      color: item.itemDetails.upcColor,
      size: item.itemDetails.upcSize,
      unitPrice: item.unitPrice,
      returnType: item.returnType,
      quantity: item.returnQuantity,
      returnReason: item.returnReason?.code,
      mosFlag: item.mosFlag,
      virtualReturnExpectedBackDate: item.virtualReturnExpectedBackDate,
      fulfillmentStore: item.fulfillmentStore,
      fulfillmentChannel: item.fulfillmentChannel, // this property may not be always available in step 1 response
      key,
    };
  }
  return {
    upc: item.upc,
    ...(item.itemDetails.returnChannel && {
      returnChannel: item.itemDetails.returnChannel,
    }),
    color: item.itemDetails.upcColor,
    size: item.itemDetails.upcSize,
    unitPrice: item.unitPrice,
    returnType: item.returnType,
    fulfillmentStore: item.fulfillmentStore,
    fulfillmentChannel: item.fulfillmentChannel,
    fulfillmentType: item.fulfillmentType,
    originalQty: item.originalQty,
    productId: item.itemDetails.productId,
    isDiscountOrCouponApplied: item.isDiscountOrCouponApplied,
    key,
  };
};

// TODO: Move payload to returnService
const prepareReturnLabelPayload = (returnDetail, rmaId) => {
  const rma = returnDetail.rmaList.find((rmaRecord) => rmaRecord.rmaId === rmaId) || {};
  const isBorisEnabled = rma.returnDropOffMethod === RETURN_METHOD_KEY_MAP.storeReturn && returnDetail.isMkpOrder;
  const isSSRE = rma.returnResolution && [RETURN_RESOLUTIONS.REPLACEMENT, RETURN_RESOLUTIONS.EXCHANGE].includes(rma.returnResolution);

  return {
    resendLabelPayload: {
      ...(rma.labelFreeReturnTrackingNumber && { labelFreeReturnTrackingNumber: rma.labelFreeReturnTrackingNumber }),
      ...(rma.returnTrackingNumber && { returnTrackingNumber: rma.returnTrackingNumber }),
      ...(rma.dropOffUrl && { dropOffUrl: rma.dropOffUrl }),
      merchandiseTotal: rma.merchandiseTotal,
      protectionPlanTotal: rma.protectionPlanTotal,
      shippingFee: rma.shippingFee,
      totalTaxes: rma.totalTaxes,
      creditedAmount: rma.creditedAmount,
      returnSubmittedDate: rma.returnSubmittedDate,
      refundMethod: rma.refundMethod,
      estimatedRefund: rma.estimatedRefund,
      orderNumber: returnDetail.orderNumber,
      eWasteFee: rma.eWasteFee,
      rmaType: rma.rmaType,
      contact: {
        emailAddress: rma.contact?.emailAddress,
        firstName: rma.contact?.firstName,
        lastName: rma.contact?.lastName,
      },
      items: rma.rmaLines?.map((rmaLine) => ({
        itemTotal: rmaLine.itemTotal,
        upc: rmaLine.upc,
        returnQuantity: rmaLine.returnQuantity,
        currentPrice: rmaLine.unitPrice,
        itemDetails: {
          upcSize: rmaLine.size,
          upcColor: rmaLine.color,
          productName: rmaLine.itemDescription,
          department: { departmentId: rmaLine.department },
          ...(returnDetail.isMkpOrder &&
            (rmaLine.sellerName || rmaLine.mkpLineItemDetails) && {
              mkpLineItemDetails: rmaLine.mkpLineItemDetails || {
                sellerName: rmaLine.sellerName,
              },
            }),
        },
      })),
      // Temporary Fix for BORIS. In Future rmaId will be used for DORIS
      pendingReturnId: !isBorisEnabled ? rma.rmaId : '',
      returnDropOffMethod: rma.returnDropOffMethod,
      refundToAccountType: rma.refundToAccountType,
      refundToAccountNumber: rma.refundToAccountNumber,
      isMkpOrder: returnDetail.isMkpOrder,
      returnAcceptanceDeadline: rma.returnAcceptanceDeadline,
      dropOffByDate: rma.dropOffByDate,
      purchaser: { encryptedGuid: returnDetail.purchaser?.encryptedGuid, encryptedId: returnDetail.purchaser?.encryptedId },
      ...(isSSRE && {
        substituteShipments: [
          {
            type: rma.returnResolution,
            substituteItems: rma.substituteLines?.map((subtitute) => ({
              upc: subtitute.upc,
              currentPrice: subtitute.unitPrice,
              itemTotal: subtitute.itemTotal,
              itemDetails: {
                productName: subtitute.itemDescription,
                upcSize: subtitute.size,
                upcColor: subtitute.color,
                department: { departmentId: subtitute.department },
                isHazmatItem: subtitute.isHazmatItem,
                quantity: subtitute.returnQuantity,
              },
            })),
            shippingContact: rma.deliveryContact,
          },
        ],
      }),
    },
    resendLabelToken: rma.resendLabelToken,
  };
};

const makeNumberAccessible = (value) => value?.toString().split('').join(' ');

const fireApiErrorTag = ({ errorCode, message, miscMessage, errorLabel }) => {
  if (import.meta.client) {
    fireTag('link', {
      event_name: 'returns error',
      error_category: 'purchases & returns',
      error_code: [errorCode],
      error_label: [errorLabel],
      error_message: [message],
      error_misc: [miscMessage],
      error_scope: ['global'],
    });
  }
};

export { getPrice, buildKey, formatPrice, prepareReturnItemsProperty, prepareReturnLabelPayload, makeNumberAccessible, fireApiErrorTag };
